import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import styles from './Form.module.css';
import {
  fetchData,
  sendData,
  updateData,
  uploadFile
} from '../../../../_metronic/helpers/utils/api';
import { createKeyValuePairs } from '../../../../_metronic/helpers/utils/utils';
import type { FormProps, FormData } from '../../../../_metronic/helpers/utils/types';
import { useLanguage } from '../../../../_metronic/helpers/utils/context';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import LinkInput from '../../../components/LinkInput';

const Form = ({ formId, heading, inputData, pageId, sectionName }: FormProps) => {
  const { language } = useLanguage();
  const initialFormData = createKeyValuePairs(inputData);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [selectedInput, setSelectedInput] = useState<string[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);

  const query = useQuery({
    queryKey: [pageId, sectionName, formId],
    queryFn: fetchData({ pageId, sectionName, key: formId })
  });

  useEffect(() => {
    const data: FormData = query?.data?.[language] ?? initialFormData;
    setFormData(data);
  }, [language, query.data]);

  const postMutation = useMutation(sendData);
  const patchMutation = useMutation(updateData);
  const uploadFileMutation = useMutation(uploadFile);

  useEffect(() => {
    if (postMutation.isSuccess || patchMutation.isSuccess) {
      setShowSuccessMessage(true);
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [postMutation.isSuccess, patchMutation.isSuccess]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [id]: value }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const { id } = event.target;
      const file = event.target?.files?.[0];
      if (file === undefined) {
        return;
      }
      setSelectedInput((prevData) => [...prevData, id]);
      const imageUrl: string = await uploadFileMutation.mutateAsync(file);
      setSelectedInput((prevData) => prevData.filter((idToRemove) => idToRemove !== id));
      setFormData((prevData) => ({ ...prevData, [id]: imageUrl }));
    } catch (error) {
      console.error('Error uploading image', error);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    const submit = async () => {
      event.preventDefault();

      const body = {
        pageId,
        sectionName,
        key: formId,
        [language]: formData
      };

      const { data } = query;
      const id: number | undefined = data?.id;

      if (data !== undefined && id !== undefined) {
        await patchMutation.mutateAsync({ id, body });
      } else {
        await postMutation.mutateAsync(body);
      }

      await query.refetch();
    };

    void submit();
  };

  const getImage = (id: string) => {
    const field = inputData.find((field) => field.id === id);

    if (!field) {
      return null;
    }

    const { backgroundColor } = field;
    const imageStyle = backgroundColor ? { backgroundColor } : {};

    if (!selectedInput.includes(id)) {
      if (formData[id] !== '' && formData[id] !== undefined) {
        return <img className={styles.image} src={formData[id]} style={imageStyle} alt='' />;
      } else {
        return <p className={`warning ${styles.message}`}>No file chosen</p>;
      }
    }

    if (uploadFileMutation.isLoading) {
      return <p className={`loading ${styles.message}`}>Uploading image...</p>;
    }

    if (
      uploadFileMutation.isError ||
      (uploadFileMutation.isSuccess && uploadFileMutation.data === undefined)
    ) {
      return <p className={`error ${styles.message}`}>Error uploading image</p>;
    }
  };
  const handleOnChange2 = (value: string, id: string) => {
    setFormData((prevData) => ({ ...prevData, [id]: value }));
  };

  const getInput = (field: typeof inputData[number]) => {
    const { id, label, type, info } = field;
    switch (type) {
      case 'input':
        if (field.hasToggle && field.toggleId) {
          return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '20px' }}>
              <input
                className={styles.input}
                id={id}
                placeholder={label}
                value={formData[id] ?? ''}
                onChange={handleOnChange}
              />
              <div className='form-check form-switch d-flex align-items-center mb-0' style={{ gap: '10px', minWidth: 'max-content' }}>
                <input
                  className='form-check-input mt-0'
                  type='checkbox'
                  id={field.toggleId}
                  checked={formData[field.toggleId] === 'true'}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      [field.toggleId!]: e.target.checked ? 'true' : 'false'
                    }))
                  }
                  style={{ width: '40px', height: '20px', cursor: 'pointer' }}
                />
                {field.toggleLabel && (
                  <label className='form-check-label' htmlFor={field.toggleId} style={{ cursor: 'pointer' }}>
                    {field.toggleLabel}
                  </label>
                )}
              </div>
            </div>
          );
        }
        if (id.toLowerCase().includes('link')) {
          return (
            <LinkInput
              className={styles.input}
              id={id}
              placeholder={label}
              value={formData[id] ?? ''}
              onChange={handleOnChange}
              onValueChange={(val) => setFormData((prev) => ({ ...prev, [id]: val }))}
            />
          );
        }
        return (
          <input
            className={styles.input}
            id={id}
            placeholder={label}
            value={formData[id] ?? ''}
            onChange={handleOnChange}
          />
        );

      case 'image':
        return (
          <div className={styles.imageInputWrap}>
            <div className={styles.fileInputWrap}>
              <input
                className={styles.fileInput}
                id={id}
                type='file'
                onChange={handleImageUpload}
              />
              <label className={styles.imageLabel} htmlFor={id}>
                Upload Image
              </label>
              <p className={styles.info}>{info}</p>
            </div>
            {getImage(id)}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            className={styles.textarea}
            placeholder={label}
            id={id}
            value={formData[id] ?? ''}
            onChange={handleOnChange}
          />
        );
      case 'inputPrivacy':
        return (
          <ReactQuill
            className={styles.quillEditor}
            theme='snow'
            value={formData[id] ?? ''}
            onChange={(value) => handleOnChange2(value, id)}
          />
        );
      case 'toggle':
        return (
          <div className='form-check form-switch'>
            <input
              className='form-check-input'
              type='checkbox'
              id={id}
              checked={formData[id] === 'true'}
              onChange={(e) =>
                setFormData((prevData) => ({ ...prevData, [id]: e.target.checked ? 'true' : 'false' }))
              }
            />
          </div>
        );

      default:
        return null;
    }
  };

  const getInputs = () => {
    return inputData.map((field) => (
      <div className={styles.inputWrap} key={field.id}>
        <label className={styles.label}>{field.label}</label>
        {getInput(field)}
      </div>
    ));
  };

  const getButtonText = () => {
    return postMutation.isLoading || patchMutation.isLoading ? 'Saving...' : 'Save Changes';
  };

  const getSubmitMessage = () => {
    if (
      postMutation.isError ||
      patchMutation.isError ||
      (postMutation.isSuccess && postMutation.data === undefined) ||
      (patchMutation.isSuccess && patchMutation.data === undefined)
    ) {
      return <p className={`error ${styles.buttonMessage}`}>Error saving changes</p>;
    }

    if (postMutation.isSuccess || patchMutation.isSuccess) {
      return <p className={`success ${styles.buttonMessage}`}>Changes saved successfully</p>;
    }
  };

  const getContent = () => {
    if (query.isLoading) {
      return <p className='loading-screen'>Fetching details...</p>;
    }

    if (query.isError) {
      return <p className='error-screen'>Error fetching details</p>;
    }

    return (
      <>
        <h1 className={styles.title}>{heading}</h1>
        <section className={styles.section}>{getInputs()}</section>
        <button
          className={`btn btn-primary btn-lg ${styles.submitButton}`}
          disabled={postMutation.isLoading || patchMutation.isLoading}
        >
          {getButtonText()}
        </button>
        {showSuccessMessage && getSubmitMessage()}
      </>
    );
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      {getContent()}
    </form>
  );
};

export default Form;
